import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import saApi from '../../services/superAdminApi';
import { FaWhatsapp } from 'react-icons/fa';
import {
  MdPerson, MdPhone, MdEmail, MdLocationOn, MdNotes,
  MdRefresh, MdContentCopy, MdCalendarToday, MdEdit, MdCheck, MdClose,
  MdAdd, MdHistory,
} from 'react-icons/md';

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'new',       label: 'New',       bg: 'rgba(96,165,250,0.12)', text: '#60a5fa' },
  { value: 'contacted', label: 'Contacted', bg: 'rgba(252,211,77,0.12)', text: '#fcd34d' },
  { value: 'converted', label: 'Converted', bg: 'rgba(52,211,153,0.12)', text: '#34d399' },
  { value: 'not_fit',   label: 'Not a Fit', bg: 'rgba(107,101,96,0.2)',  text: '#8a847c' },
];

const FILTER_OPTIONS = [
  { value: 'all',            label: 'All'             },
  { value: 'needs_followup', label: 'Needs Follow-up' },
  { value: 'no_contact',     label: 'No Contact Yet'  },
  { value: 'new',            label: 'New'             },
  { value: 'contacted',      label: 'Contacted'       },
  { value: 'converted',      label: 'Converted'       },
  { value: 'not_fit',        label: 'Not a Fit'       },
];

const CHANNEL_OPTIONS = [
  { value: 'whatsapp',  label: 'WhatsApp'  },
  { value: 'phone',     label: 'Phone'     },
  { value: 'email',     label: 'Email'     },
  { value: 'messenger', label: 'Messenger' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'in_person', label: 'In Person' },
  { value: 'other',     label: 'Other'     },
];

const MESSAGE_TYPE_OPTIONS = [
  { value: 'demo_link',  label: 'Sent Demo Link' },
  { value: 'follow_up',  label: 'Follow-up'      },
  { value: 'pricing',    label: 'Pricing Info'   },
  { value: 'onboarding', label: 'Onboarding'     },
  { value: 'custom',     label: 'Other'          },
];

const CHANNEL_LABELS   = Object.fromEntries(CHANNEL_OPTIONS.map(o => [o.value, o.label]));
const MSG_TYPE_LABELS  = Object.fromEntries(MESSAGE_TYPE_OPTIONS.map(o => [o.value, o.label]));

const SOURCE_LABELS = {
  demo:    'Demo Page',
  landing: 'Landing Page',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function statusStyle(value) {
  return STATUS_OPTIONS.find(o => o.value === value) || STATUS_OPTIONS[0];
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function toDateInput(d) {
  if (!d) return '';
  const date = new Date(d);
  const y    = date.getFullYear();
  const m    = String(date.getMonth() + 1).padStart(2, '0');
  const day  = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getFollowUpUrgency(dateVal) {
  if (!dateVal) return null;
  const dateStr  = new Date(dateVal).toLocaleDateString('en-CA');
  const todayStr = new Date().toLocaleDateString('en-CA');
  if (dateStr < todayStr) return 'overdue';
  if (dateStr === todayStr) return 'today';
  return 'upcoming';
}

function fmtFollowUpDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function timeAgo(ts) {
  if (!ts) return null;
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 2)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7)   return `${days}d ago`;
  return fmtDate(ts);
}

// ── Follow-up urgency badge ───────────────────────────────────────────────────
function FollowUpBadge({ urgency, dateVal }) {
  if (!urgency) return null;
  const styles = {
    overdue:  { background: 'rgb(var(--color-primary-rgb) / 0.12)', color: 'var(--color-primary)', label: 'Overdue'         },
    today:    { background: 'rgba(252,211,77,0.12)', color: '#fcd34d', label: 'Follow up today' },
    upcoming: { background: 'rgba(96,165,250,0.12)', color: '#93c5fd', label: fmtFollowUpDate(dateVal) },
  };
  const s = styles[urgency];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      background: s.background, color: s.color,
      borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 700,
      whiteSpace: 'nowrap',
    }}>
      <MdCalendarToday style={{ fontSize: 10 }} />
      {s.label}
    </span>
  );
}

// ── Follow-up date cell ───────────────────────────────────────────────────────
function FollowUpDateCell({ leadId, value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate]       = useState(toDateInput(value));
  const urgency               = getFollowUpUrgency(value);

  const { mutate, isPending } = useMutation({
    mutationFn: (nextFollowUpAt) =>
      saApi.patch(`/api/super-admin/leads/${leadId}`, { nextFollowUpAt }).then(r => r.data),
    onSuccess: () => { setEditing(false); onSaved(); toast.success('Follow-up date saved'); },
    onError:   () => toast.error('Failed to save date'),
  });

  if (editing) {
    return (
      <div className="flex flex-col gap-1">
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          autoFocus
          style={{ background: '#1a1918', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '3px 7px', fontSize: 12, outline: 'none' }}
        />
        <div className="flex gap-1">
          <button
            onClick={() => mutate(date || null)}
            disabled={isPending}
            style={{ background: '#c8102e', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 7px', fontSize: 11, cursor: 'pointer' }}
          >
            <MdCheck />
          </button>
          <button
            onClick={() => { setDate(toDateInput(value)); setEditing(false); }}
            style={{ background: '#1a1918', color: '#8a847c', border: 'none', borderRadius: 4, padding: '2px 7px', fontSize: 11, cursor: 'pointer' }}
          >
            <MdClose />
          </button>
          {value && (
            <button
              onClick={() => mutate(null)}
              style={{ background: '#1a1918', color: '#6b6560', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer' }}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {urgency ? (
        <FollowUpBadge urgency={urgency} dateVal={value} />
      ) : (
        <span style={{ color: '#6b6560', fontSize: 11 }}>No date set</span>
      )}
      <button
        onClick={() => setEditing(true)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#6b6560', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: 0 }}
      >
        <MdEdit style={{ fontSize: 12 }} />
        {value ? 'Change' : 'Set date'}
      </button>
    </div>
  );
}

// ── Follow-up notes cell ──────────────────────────────────────────────────────
function FollowUpNotesCell({ leadId, initial, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText]       = useState(initial || '');

  const { mutate, isPending } = useMutation({
    mutationFn: (followUpNotes) =>
      saApi.patch(`/api/super-admin/leads/${leadId}`, { followUpNotes }).then(r => r.data),
    onSuccess: () => { setEditing(false); onSaved(); toast.success('Follow-up note saved'); },
    onError:   () => toast.error('Failed to save note'),
  });

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-left text-xs max-w-[180px]"
        style={{
          color: text ? '#8a847c' : '#6b6560',
          background: 'none', border: 'none', cursor: 'pointer',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', whiteSpace: 'pre-line',
        }}
        title={text || 'Add follow-up notes'}
      >
        {text || '+ Add follow-up notes'}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 min-w-[200px]">
      <textarea
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        maxLength={2000}
        rows={3}
        placeholder="What was discussed, what's next…"
        style={{ background: '#1a1918', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '5px 8px', fontSize: 12, resize: 'vertical' }}
      />
      <div className="flex gap-1">
        <button
          onClick={() => mutate(text)}
          disabled={isPending}
          style={{ background: '#c8102e', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
        >
          {isPending ? '…' : 'Save'}
        </button>
        <button
          onClick={() => { setText(initial || ''); setEditing(false); }}
          style={{ background: '#1a1918', color: '#8a847c', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Internal note cell ────────────────────────────────────────────────────────
function NoteCell({ leadId, initial, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText]       = useState(initial || '');

  const { mutate, isPending } = useMutation({
    mutationFn: (notes) => saApi.patch(`/api/super-admin/leads/${leadId}`, { notes }).then(r => r.data),
    onSuccess:  () => { setEditing(false); onSaved(); toast.success('Note saved'); },
    onError:    () => toast.error('Failed to save note'),
  });

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-left text-xs max-w-[180px] truncate"
        style={{ color: text ? '#ede8e0' : '#6b6560', background: 'none', border: 'none', cursor: 'pointer' }}
        title={text || 'Add note'}
      >
        {text || '+ Add note'}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 min-w-[180px]">
      <textarea
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        maxLength={1000}
        rows={2}
        style={{ background: '#1a1918', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '4px 8px', fontSize: 12, resize: 'none' }}
      />
      <div className="flex gap-1">
        <button
          onClick={() => mutate(text)}
          disabled={isPending}
          style={{ background: '#c8102e', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
        >
          {isPending ? '…' : 'Save'}
        </button>
        <button
          onClick={() => { setText(initial || ''); setEditing(false); }}
          style={{ background: '#1a1918', color: '#8a847c', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Status dropdown ───────────────────────────────────────────────────────────
function StatusDropdown({ leadId, current, onSaved }) {
  const { mutate, isPending } = useMutation({
    mutationFn: (status) => saApi.patch(`/api/super-admin/leads/${leadId}`, { status }).then(r => r.data),
    onSuccess:  () => { onSaved(); toast.success('Status updated'); },
    onError:    () => toast.error('Failed to update status'),
  });

  const st = statusStyle(current);

  return (
    <select
      value={current}
      disabled={isPending}
      onChange={e => mutate(e.target.value)}
      style={{
        background: st.bg, color: st.text, border: 'none', borderRadius: 6,
        padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
        appearance: 'none', paddingRight: 12,
      }}
    >
      {STATUS_OPTIONS.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ── Log Contact Modal ─────────────────────────────────────────────────────────
function LogContactModal({ lead, onClose, onSaved }) {
  const [channel,      setChannel]      = useState('whatsapp');
  const [messageType,  setMessageType]  = useState('follow_up');
  const [note,         setNote]         = useState('');
  const [nextFollowUp, setNextFollowUp] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: (body) =>
      saApi.post(`/api/super-admin/leads/${lead._id}/contact-log`, body).then(r => r.data),
    onSuccess: () => { onSaved(); onClose(); toast.success('Contact logged'); },
    onError:   () => toast.error('Failed to log contact'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutate({ channel, messageType, note: note.trim(), nextFollowUpAt: nextFollowUp || undefined });
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 420 }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-[#ede8e0] text-sm">Log Contact</h3>
            <p style={{ color: '#6b6560', fontSize: 11 }}>
              {lead.name}{lead.clubName ? ` · ${lead.clubName}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ color: '#6b6560', background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={{ color: '#8a847c', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Channel</label>
              <select
                value={channel}
                onChange={e => setChannel(e.target.value)}
                style={{ width: '100%', background: '#1a1918', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '6px 8px', fontSize: 12, outline: 'none' }}
              >
                {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ color: '#8a847c', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Type</label>
              <select
                value={messageType}
                onChange={e => setMessageType(e.target.value)}
                style={{ width: '100%', background: '#1a1918', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '6px 8px', fontSize: 12, outline: 'none' }}
              >
                {MESSAGE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={{ color: '#8a847c', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Note (optional)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="What was discussed…"
              style={{ width: '100%', background: '#1a1918', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '6px 8px', fontSize: 12, resize: 'none', outline: 'none' }}
            />
          </div>

          <div>
            <label style={{ color: '#8a847c', fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Next Follow-up Date (optional)
            </label>
            <input
              type="date"
              value={nextFollowUp}
              onChange={e => setNextFollowUp(e.target.value)}
              style={{ background: '#1a1918', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none' }}
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={isPending}
              style={{ flex: 1, background: '#c8102e', color: '#fff', border: 'none', borderRadius: 6, padding: '8px', fontSize: 12, fontWeight: 600, cursor: isPending ? 'not-allowed' : 'pointer', opacity: isPending ? 0.7 : 1 }}
            >
              {isPending ? 'Saving…' : 'Save Log'}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{ background: '#1a1918', color: '#8a847c', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 12, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Contact log history strip ─────────────────────────────────────────────────
function ContactLogHistory({ logs }) {
  const recent = [...(logs || [])].reverse().slice(0, 3);
  if (!recent.length) return null;
  return (
    <div className="space-y-1 mt-2">
      {recent.map((log, i) => (
        <div key={i} style={{ background: '#1a1918', borderRadius: 6, padding: '5px 8px', fontSize: 11 }}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span style={{ color: '#93c5fd', fontWeight: 600 }}>{CHANNEL_LABELS[log.channel] || log.channel}</span>
            <span style={{ color: '#6b6560' }}>·</span>
            <span style={{ color: '#6b6560' }}>{MSG_TYPE_LABELS[log.messageType] || log.messageType}</span>
            <span style={{ color: '#6b6560' }}>· {timeAgo(log.contactedAt)}</span>
          </div>
          {log.note && <p style={{ color: '#8a847c', marginTop: 2 }}>{log.note}</p>}
        </div>
      ))}
    </div>
  );
}

// ── WhatsApp / Copy button ────────────────────────────────────────────────────
function buildWaMessage(lead) {
  const clubLine = lead.clubName ? `I saw your request for ${lead.clubName}.\n` : '';
  return (
    `Namaste ${lead.name},\n` +
    `Thank you for your interest in TrainAxis.\n\n` +
    `${clubLine}` +
    `TrainAxis helps clubs manage students, attendance, monthly fees, QR payments, parent reminders, and reports.\n\n` +
    `Would you like a quick demo/setup call?\n\n` +
    `- TrainAxis Team`
  );
}

function formatNepalPhone(raw) {
  if (!raw) return '';
  const digits = raw.replace(/[^\d]/g, '');
  if (!digits) return '';
  if (digits.startsWith('977') && digits.length >= 12) return digits;
  if (digits.startsWith('0') && digits.length >= 10)   return '977' + digits.slice(1);
  if (digits.length === 10 && (digits.startsWith('98') || digits.startsWith('97'))) return '977' + digits;
  return digits;
}

function FollowUpButton({ lead, onSaved, compact = false }) {
  const phone = lead.phone?.trim();
  const msg   = buildWaMessage(lead);

  const { mutate: markContacted } = useMutation({
    mutationFn: () => saApi.patch(`/api/super-admin/leads/${lead._id}`, { status: 'contacted' }).then(r => r.data),
    onSuccess: onSaved,
  });

  const { mutate: quickLog } = useMutation({
    mutationFn: (body) =>
      saApi.post(`/api/super-admin/leads/${lead._id}/contact-log`, body).then(r => r.data),
    onSuccess: () => { onSaved(); toast.success('Contact logged'); },
    onError:   () => toast.error('Failed to log contact'),
  });

  if (phone) {
    const waNumber = formatNepalPhone(phone);
    const waUrl    = `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;

    const handleClick = (e) => {
      e.preventDefault();
      if (lead.status === 'new') markContacted();
      window.open(waUrl, '_blank', 'noopener,noreferrer');
      toast((t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <span style={{ color: '#ede8e0' }}>WhatsApp draft opened</span>
          <button
            onClick={() => {
              quickLog({ channel: 'whatsapp', messageType: 'follow_up', note: 'WhatsApp draft opened' });
              toast.dismiss(t.id);
            }}
            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Log contact
          </button>
        </div>
      ), { duration: 8000 });
    };

    return (
      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        title={`WhatsApp ${phone}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: compact ? 4 : 5,
          background: '#16a34a', color: '#fff',
          border: 'none', borderRadius: 6,
          padding: compact ? '3px 8px' : '5px 10px',
          fontSize: compact ? 11 : 12, fontWeight: 600, cursor: 'pointer',
          textDecoration: 'none', whiteSpace: 'nowrap',
        }}
      >
        <FaWhatsapp style={{ fontSize: compact ? 12 : 14, flexShrink: 0 }} />
        WhatsApp
      </a>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(msg)
      .then(() => toast.success('Message copied to clipboard'))
      .catch(() => toast.error('Copy failed'));
  };

  return (
    <button
      onClick={handleCopy}
      title="Copy follow-up message"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: compact ? 4 : 5,
        background: '#1a1918', color: '#8a847c',
        border: '1px solid rgba(237,232,224,0.08)', borderRadius: 6,
        padding: compact ? '3px 8px' : '5px 10px',
        fontSize: compact ? 11 : 12, fontWeight: 600, cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <MdContentCopy style={{ fontSize: compact ? 12 : 14, flexShrink: 0 }} />
      Copy Msg
    </button>
  );
}

// ── Mobile card ───────────────────────────────────────────────────────────────
function LeadCard({ lead, onSaved }) {
  const [showLogModal, setShowLogModal] = useState(false);
  const [showLogs,     setShowLogs]     = useState(false);
  const urgency = getFollowUpUrgency(lead.nextFollowUpAt);

  const lastLog     = lead.contactLogs?.length > 0 ? lead.contactLogs[lead.contactLogs.length - 1] : null;
  const logsCount   = lead.contactLogs?.length || 0;

  return (
    <div
      style={{
        background: '#1e1d1b',
        border: urgency === 'overdue' ? '1px solid rgba(255,69,58,0.2)' : urgency === 'today' ? '1px solid rgba(252,211,77,0.2)' : '1px solid rgba(237,232,224,0.08)',
        borderRadius: 12, padding: '16px',
      }}
      className="space-y-3"
    >
      {/* Name + status */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-[#ede8e0] text-sm">{lead.name}</p>
          {lead.clubName && <p className="text-xs mt-0.5" style={{ color: '#8a847c' }}>{lead.clubName}</p>}
        </div>
        <StatusDropdown leadId={lead._id} current={lead.status} onSaved={onSaved} />
      </div>

      {/* Contact info */}
      <div className="space-y-1 text-xs" style={{ color: '#8a847c' }}>
        {lead.phone && (
          <div className="flex items-center gap-1.5">
            <MdPhone style={{ color: '#6b6560', flexShrink: 0 }} />
            <span>{lead.phone}</span>
          </div>
        )}
        {lead.email && (
          <div className="flex items-center gap-1.5">
            <MdEmail style={{ color: '#6b6560', flexShrink: 0 }} />
            <span className="break-all">{lead.email}</span>
          </div>
        )}
        {lead.city && (
          <div className="flex items-center gap-1.5">
            <MdLocationOn style={{ color: '#6b6560', flexShrink: 0 }} />
            <span>{lead.city}</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <FollowUpButton lead={lead} onSaved={onSaved} />
        <button
          onClick={() => setShowLogModal(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          <MdAdd style={{ fontSize: 13 }} /> Log Contact
        </button>
      </div>

      {/* Last contacted */}
      {lead.lastContactedAt && (
        <div className="flex items-center gap-1.5" style={{ color: '#6b6560', fontSize: 11 }}>
          <MdHistory style={{ fontSize: 12, color: '#6b6560', flexShrink: 0 }} />
          <span>
            Last contacted: {timeAgo(lead.lastContactedAt)}
            {lastLog ? ` via ${CHANNEL_LABELS[lastLog.channel] || lastLog.channel}` : ''}
          </span>
        </div>
      )}

      {/* Follow-up date */}
      <div>
        <p className="text-xs font-semibold mb-1" style={{ color: '#6b6560' }}>Next Follow-up</p>
        <FollowUpDateCell leadId={lead._id} value={lead.nextFollowUpAt} onSaved={onSaved} />
      </div>

      {/* Source + date */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs" style={{ color: '#6b6560' }}>
          {SOURCE_LABELS[lead.source] || lead.source} · {fmtDate(lead.createdAt)}
        </span>
      </div>

      {/* Message preview */}
      {lead.message && (
        <p className="text-xs leading-relaxed" style={{ color: '#8a847c', background: '#1a1918', padding: '8px 12px', borderRadius: 6 }}>
          "{lead.message}"
        </p>
      )}

      {/* Internal note */}
      <div>
        <p className="text-xs font-semibold mb-1" style={{ color: '#6b6560' }}>
          <MdNotes style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
          Note
        </p>
        <NoteCell leadId={lead._id} initial={lead.notes} onSaved={onSaved} />
      </div>

      {/* Follow-up notes */}
      <div>
        <p className="text-xs font-semibold mb-1" style={{ color: '#6b6560' }}>
          <MdCalendarToday style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
          Follow-up Notes
        </p>
        <FollowUpNotesCell leadId={lead._id} initial={lead.followUpNotes} onSaved={onSaved} />
      </div>

      {/* Contact log history (expandable) */}
      {logsCount > 0 && (
        <div>
          <button
            onClick={() => setShowLogs(p => !p)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#6b6560', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: 0 }}
          >
            <MdHistory style={{ fontSize: 12 }} />
            {showLogs ? 'Hide history' : `Show history (${logsCount})`}
          </button>
          {showLogs && <ContactLogHistory logs={lead.contactLogs} />}
        </div>
      )}

      {showLogModal && (
        <LogContactModal lead={lead} onClose={() => setShowLogModal(false)} onSaved={onSaved} />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SalesLeads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient  = useQueryClient();
  const statusFilter = searchParams.get('status') || 'all';
  const demoSlug     = searchParams.get('demoSlug') || '';

  const [logModalLead, setLogModalLead] = useState(null);

  const setStatusFilter = (val) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val === 'all') next.delete('status');
      else next.set('status', val);
      return next;
    });
  };

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['sa-leads', statusFilter, demoSlug],
    queryFn:  () => saApi.get('/api/super-admin/leads', {
      params: { status: statusFilter, ...(demoSlug ? { demoSlug } : {}) },
    }).then(r => r.data),
    staleTime: 1000 * 30,
  });

  const { data: summaryData } = useQuery({
    queryKey: ['sa-sales-summary'],
    queryFn:  () => saApi.get('/api/super-admin/sales-summary').then(r => r.data),
    staleTime: 60000,
  });
  const followUpsDueCount = summaryData?.followUpsDueCount || 0;

  const leads    = data?.leads || [];
  const total    = data?.total || 0;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sa-leads'] });

  return (
    <div className="p-5 max-w-6xl mx-auto">

      {/* Log Contact Modal (table-row triggered) */}
      {logModalLead && (
        <LogContactModal
          lead={logModalLead}
          onClose={() => setLogModalLead(null)}
          onSaved={() => { invalidate(); queryClient.invalidateQueries({ queryKey: ['sa-sales-summary'] }); }}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-[#ede8e0]">Sales Leads</h1>
          <p className="text-xs mt-0.5" style={{ color: '#6b6560' }}>
            {total} total · Setup requests from demo and landing page
          </p>
        </div>
        <div className="flex items-center gap-2">
          {demoSlug && (
            <button
              onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('demoSlug'); return next; })}
              style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              /demo/{demoSlug} ×
            </button>
          )}
          <button
            onClick={() => refetch()}
            style={{ background: '#1a1918', color: '#8a847c', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
          >
            <MdRefresh /> Refresh
          </button>
        </div>
      </div>

      {/* Follow-ups due amber banner */}
      {followUpsDueCount > 0 && statusFilter !== 'needs_followup' && (
        <div
          style={{ background: 'rgba(252,211,77,0.12)', border: '1px solid rgba(252,211,77,0.2)', borderRadius: 8, padding: '8px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
        >
          <span style={{ color: '#fcd34d', fontSize: 12, fontWeight: 600 }}>
            {followUpsDueCount} follow-up{followUpsDueCount !== 1 ? 's' : ''} due today or overdue
          </span>
          <button
            onClick={() => setStatusFilter('needs_followup')}
            style={{ color: '#fbbf24', fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', whiteSpace: 'nowrap' }}
          >
            View →
          </button>
        </div>
      )}

      {/* Status filter tabs */}
      <div className="flex gap-1.5 flex-wrap mb-5">
        {FILTER_OPTIONS.map(o => {
          const active          = statusFilter === o.value;
          const isNeedsFollowup = o.value === 'needs_followup';
          const isNoContact     = o.value === 'no_contact';
          return (
            <button
              key={o.value}
              onClick={() => setStatusFilter(o.value)}
              style={{
                background: active ? (isNeedsFollowup ? 'rgba(252,211,77,0.12)' : isNoContact ? 'rgba(96,165,250,0.12)' : '#c8102e') : '#1e1d1b',
                color:      active ? (isNeedsFollowup ? '#fcd34d' : isNoContact ? '#93c5fd' : '#fff')    : '#8a847c',
                border:     active ? 'none' : '1px solid rgba(237,232,224,0.08)',
                borderRadius: 8, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {isPending && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 12, height: 56 }} className="animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="text-center py-12" style={{ color: 'var(--color-primary)' }}>
          Failed to load leads.
          <button onClick={() => refetch()} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* Empty */}
      {!isPending && !isError && leads.length === 0 && (
        <div className="text-center py-16" style={{ color: '#6b6560' }}>
          <MdPerson style={{ fontSize: 40, margin: '0 auto 12px', display: 'block', color: '#6b6560' }} />
          <p className="font-semibold" style={{ color: '#8a847c' }}>
            {statusFilter === 'needs_followup' ? 'No overdue follow-ups'
              : statusFilter === 'no_contact' ? 'No uncontacted leads'
              : 'No leads yet'}
          </p>
          <p className="text-xs mt-1">
            {statusFilter === 'needs_followup'
              ? 'Leads with a past or today follow-up date will appear here.'
              : statusFilter === 'no_contact'
              ? 'New leads with no contact logged will appear here.'
              : 'Leads appear here when someone submits a Request Setup form.'}
          </p>
        </div>
      )}

      {/* Desktop table */}
      {!isPending && !isError && leads.length > 0 && (
        <>
          <div className="hidden lg:block overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(237,232,224,0.08)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                  {['Name / Club', 'Contact', 'City', 'Source', 'Note', 'Follow-up', 'Actions', 'Status', 'Date'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#6b6560', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#1a1918' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => {
                  const urgency = getFollowUpUrgency(lead.nextFollowUpAt);
                  const lastLog = lead.contactLogs?.length > 0
                    ? lead.contactLogs[lead.contactLogs.length - 1]
                    : null;
                  return (
                    <tr
                      key={lead._id}
                      style={{
                        borderBottom: '1px solid rgba(237,232,224,0.05)',
                        background: urgency === 'overdue' ? 'rgba(255,69,58,0.08)' : urgency === 'today' ? 'rgba(252,211,77,0.08)' : undefined,
                      }}
                    >
                      <td style={{ padding: '12px 14px' }}>
                        <p className="font-semibold text-[#ede8e0]">{lead.name}</p>
                        {lead.clubName && <p style={{ color: '#8a847c', fontSize: 11, marginTop: 2 }}>{lead.clubName}</p>}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {lead.phone && <p style={{ color: '#ede8e0' }}>{lead.phone}</p>}
                        {lead.email && <p style={{ color: '#8a847c', fontSize: 11, marginTop: 2 }}>{lead.email}</p>}
                      </td>
                      <td style={{ padding: '12px 14px', color: '#8a847c' }}>{lead.city || '—'}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ background: '#1a1918', color: '#6b6560', borderRadius: 4, padding: '2px 6px', fontSize: 11 }}>
                          {SOURCE_LABELS[lead.source] || lead.source}
                        </span>
                        {lead.demoSlug && (
                          <p style={{ color: '#6b6560', fontFamily: 'monospace', fontSize: 10, marginTop: 2 }}>/demo/{lead.demoSlug}</p>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <NoteCell leadId={lead._id} initial={lead.notes} onSaved={invalidate} />
                      </td>
                      <td style={{ padding: '12px 14px', minWidth: 180 }}>
                        <div className="space-y-2">
                          <FollowUpDateCell leadId={lead._id} value={lead.nextFollowUpAt} onSaved={invalidate} />
                          <FollowUpNotesCell leadId={lead._id} initial={lead.followUpNotes} onSaved={invalidate} />
                          {lead.lastContactedAt && (
                            <p style={{ color: '#6b6560', fontSize: 10 }}>
                              Last contact: {timeAgo(lead.lastContactedAt)}
                              {lastLog ? ` via ${CHANNEL_LABELS[lastLog.channel] || lastLog.channel}` : ''}
                            </p>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div className="flex flex-col gap-1.5">
                          <FollowUpButton lead={lead} onSaved={invalidate} compact />
                          <button
                            onClick={() => setLogModalLead(lead)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 5, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            <MdAdd style={{ fontSize: 12 }} /> Log Contact
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <StatusDropdown leadId={lead._id} current={lead.status} onSaved={invalidate} />
                      </td>
                      <td style={{ padding: '12px 14px', color: '#6b6560', whiteSpace: 'nowrap' }}>{fmtDate(lead.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {leads.map(lead => (
              <LeadCard key={lead._id} lead={lead} onSaved={invalidate} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
